import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { type RootState } from '../../store/redux/store/store';
import { QRCodeSVG } from 'qrcode.react';
import {
  Lock,
  Share2,
  Download,
  Flag,
  Check,
  Loader2,
  AlertCircle,
  Settings,
  User,
  Send
} from 'lucide-react';
import ShareMenuModal from './components/ShareMenuModal';
import ReportModal from './components/ReportModal';
import ContactDrawer from './components/ContactDrawer';
import { getFullPublicProfile, recordProfileView, getMyProfiles, PublicProfileDetailView } from '../../api/PublicProfile/publicProfileApi';
import { Card, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import ProfileCard, { type ProfileDisplayData, getProfileBackgroundStyle } from '../../components/ProfileCard';

import { getFullUrl as getImageUrl } from '../../utils/url';

// No-op stub until a real analytics client is wired in.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function trackAnalytics(event: string, payload?: Record<string, unknown>): void {
  // intentionally empty
}

// Internal UI data structure - mapped from API response
interface Achievement {
  title: string;
  date: string;
  icon: string;
}

interface SocialLink {
  platform: string;
  url: string;
  icon: string;
}

interface ColorScheme {
  primary: string;
  secondary: string;
}

interface CompanyBranding {
  companyLogoUrl?: string;
  primaryColor: string;
  secondaryColor: string;
  disclaimerText?: string;
  footerText?: string;
  showCompanyLogo: boolean;
  showDisclaimer: boolean;
}

interface ProfileData {
  profileId: string;
  slug: string;
  employeeId: string;
  firstName: string;
  lastName: string;
  fullName: string;
  jobTitle: string;
  department: string;
  photo: string | null;
  email: string;
  phone: string;
  location: string;
  bio: string;
  skills: string[];
  achievements: Achievement[];
  socialLinks: SocialLink[];
  companyName: string;
  companyLogo: string;
  isPublic: boolean;
  showEmail: boolean;
  showPhone: boolean;
  showSkills: boolean;
  showAchievements: boolean;
  isVerified: boolean;
  colorScheme: ColorScheme;
  profileType: string;
  language?: string;
  branding?: CompanyBranding;
}

/**
 * Maps API response (PublicProfileDetailView) to internal UI ProfileData structure
 */
const mapApiResponseToProfileData = (apiData: PublicProfileDetailView): ProfileData => {
  // Parse color scheme - API returns string like "blue" or "blue,indigo" or JSON
  let colorScheme: ColorScheme = { primary: 'blue', secondary: 'indigo' };
  if (apiData.colorScheme) {
    try {
      // Try parsing as JSON first
      const parsed = JSON.parse(apiData.colorScheme);
      if (parsed.primary && parsed.secondary) {
        colorScheme = parsed;
      }
    } catch {
      // Fallback: treat as simple color name or comma-separated
      const colors = apiData.colorScheme.split(',');
      colorScheme = {
        primary: colors[0]?.trim() || 'blue',
        secondary: colors[1]?.trim() || 'indigo'
      };
    }
  }

  return {
    profileId: apiData.id.toString(),
    slug: apiData.slug,
    employeeId: apiData.user?.id || '',
    firstName: apiData.user?.firstName || '',
    lastName: apiData.user?.lastName || '',
    fullName: apiData.user?.fullName || `${apiData.user?.firstName || ''} ${apiData.user?.lastName || ''}`.trim(),
    jobTitle: apiData.customJobTitle || apiData.user?.title || '',
    department: apiData.user?.departmentName || '',
    photo: apiData.user?.imageUrl || null,
    email: apiData.user?.email || '',
    phone: apiData.user?.phoneNumber || '',
    location: apiData.customLocation || '',
    bio: apiData.bio || '',
    skills: apiData.skills?.map(s => s.skillName) || [],
    achievements: apiData.achievements?.map(a => ({
      title: a.title,
      date: a.date || '',
      icon: a.iconName || 'Award'
    })) || [],
    socialLinks: apiData.socialLinks?.map(s => ({
      platform: s.platform,
      url: s.url,
      icon: s.iconName || s.platform
    })) || [],
    companyName: apiData.company?.name || '',
    companyLogo: apiData.company?.imageUrl || '',
    isPublic: apiData.isPublic,
    showEmail: apiData.showEmail,
    showPhone: apiData.showPhone,
    showSkills: apiData.showSkills,
    showAchievements: apiData.showAchievements,
    // No real verification signal exists — ProfileStatus.Active only means the
    // profile is "published", not identity-verified. Deriving the badge from
    // status showed a "Verified" trust badge on every published profile, which
    // is a fake trust signal. Keep it off until a genuine verification signal
    // is added; do NOT re-derive from status.
    isVerified: false,
    colorScheme,
    profileType: apiData.personaName || 'Professional',
    language: apiData.language || undefined,
    // Include company branding if available
    branding: apiData.branding ? {
      companyLogoUrl: apiData.branding.companyLogoUrl,
      primaryColor: apiData.branding.primaryColor,
      secondaryColor: apiData.branding.secondaryColor,
      disclaimerText: apiData.branding.disclaimerText,
      footerText: apiData.branding.footerText,
      showCompanyLogo: apiData.branding.showCompanyLogo,
      showDisclaimer: apiData.branding.showDisclaimer
    } : undefined
  };
};

interface ThemeColors {
  from: string;
  to: string;
  bgLight: string;
  bg: string;
  text: string;
  badge: string;
}

const PublicProfileDisplay: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const profileRef = useRef<HTMLDivElement>(null);
  const qrCodeRef = useRef<HTMLDivElement>(null);

  // Check if user is authenticated
  const isAuthenticated = useSelector((state: RootState) => state.user?.isLoggedIn === true);

  // State management
  const [profiles, setProfiles] = useState<ProfileData[]>([]);
  const [currentProfileIndex, setCurrentProfileIndex] = useState(0);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showContactDrawer, setShowContactDrawer] = useState(false);
  const [copiedToast] = useState(false);

  // Loading and error states
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Distinct from `error`: a genuine 404 (slug resolves to no public profile).
  // Rendered as not-found copy with NO retry CTA, since retrying can't help.
  const [notFound, setNotFound] = useState(false);
  const [showNoProfileCTA, setShowNoProfileCTA] = useState(false);

  // Fetch profile data from API
  useEffect(() => {
    const fetchProfile = async () => {
      // Internal viewer: no slug provided, signed-in user viewing their own personas.
      // Load every persona-profile the user owns so the dropdown can switch between them.
      if (!slug) {
        if (isAuthenticated) {
          try {
            setIsLoading(true);
            const myProfilesResult = await getMyProfiles();

            if (myProfilesResult.status === 200 && myProfilesResult.payload && myProfilesResult.payload.length > 0) {
              const fullResults = await Promise.all(
                myProfilesResult.payload.map((p) => getFullPublicProfile(p.slug).catch(() => null))
              );

              const mapped = fullResults
                .filter((r): r is NonNullable<typeof r> => !!r && r.status === 200 && !!r.payload)
                .map((r) => mapApiResponseToProfileData(r.payload!));

              if (mapped.length > 0) {
                setProfiles(mapped);
              } else {
                setShowNoProfileCTA(true);
              }
            } else {
              setShowNoProfileCTA(true);
            }
          } catch (err) {
            console.error('Failed to fetch user profile:', err);
            setShowNoProfileCTA(true);
          } finally {
            setIsLoading(false);
          }
        } else {
          setError('No profile slug provided');
          setIsLoading(false);
        }
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        setNotFound(false);

        const result = await getFullPublicProfile(slug);

        if (result.status === 200 && result.payload) {
          const mappedProfile = mapApiResponseToProfileData(result.payload);
          setProfiles([mappedProfile]);

          recordProfileView(slug).catch(console.error);
        } else if (result.status === 404) {
          // Slug doesn't resolve to a public profile — a true 404, not a
          // transient failure. Show not-found copy (no retry).
          setNotFound(true);
        } else {
          const errorMessage = result.errors?.[0]?.description || 'Profile not found';
          setError(errorMessage);
        }
      } catch (err) {
        console.error('Failed to fetch profile:', err);
        // The request layer rejects a 404 with the parsed Result body
        // (which carries `status`). Treat that as not-found (no retry CTA);
        // only genuine transient failures get the "try again" error state.
        const status = (err as { status?: number; response?: { status?: number } })?.status
          ?? (err as { response?: { status?: number } })?.response?.status;
        if (status === 404) {
          setNotFound(true);
        } else {
          setError('Failed to load profile. Please try again later.');
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfile();
  }, [slug, isAuthenticated]);

  const profileData = profiles?.[currentProfileIndex];
  const profileUrl = `${window.location?.origin}/p/${profileData?.slug || slug}`;

  // SEO Meta Tags - only update when profile data is available
  useEffect(() => {
    if (!profileData) return;

    document.title = `${profileData?.fullName} - ${profileData?.jobTitle} | ${profileData?.companyName}`;

    // Update meta tags
    const metaTags = [
      { property: 'og:title', content: `${profileData?.fullName} - ${profileData?.jobTitle}` },
      { property: 'og:description', content: profileData?.bio || `Professional profile of ${profileData?.fullName}` },
      { property: 'og:image', content: getImageUrl(profileData?.photo) || `${window.location?.origin}/assets/images/mowazaf_Logo-1763289947611.png` },
      { property: 'og:url', content: profileUrl },
      { property: 'og:type', content: 'profile' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: `${profileData?.fullName} - ${profileData?.jobTitle}` },
      { name: 'twitter:description', content: profileData?.bio || `Professional profile of ${profileData?.fullName}` },
      { name: 'description', content: profileData?.bio || `Professional profile of ${profileData?.fullName}` }
    ];

    metaTags.forEach((tag) => {
      const existingTag = document.querySelector(
        tag.property ? `meta[property="${tag.property}"]` : `meta[name="${tag.name}"]`
      );

      if (existingTag) {
        existingTag.setAttribute('content', tag.content);
      } else {
        const newTag = document.createElement('meta');
        if (tag.property) newTag.setAttribute('property', tag.property);
        if (tag.name) newTag.setAttribute('name', tag.name);
        newTag.setAttribute('content', tag.content);
        document.head.appendChild(newTag);
      }
    });

    // Note: Profile view analytics are tracked via API (recordProfileView)
    // For client-side analytics (GA, Mixpanel), integrate here:
    // gtag('event', 'profile_view', { profileId: profileData?.profileId });
  }, [profileData, profileUrl]);

  // vCard generation
  const generateVCard = () => {
    const vCard = [
      'BEGIN:VCARD',
      'VERSION:3.0',
      `FN:${profileData?.fullName}`,
      `N:${profileData?.lastName};${profileData?.firstName};;;`,
      `ORG:${profileData?.companyName}`,
      `TITLE:${profileData?.jobTitle}`,
      `EMAIL:${profileData?.email}`,
      `TEL:${profileData?.phone}`,
      `ADR:;;${profileData?.location};;;;`,
      `NOTE:${profileData?.bio || ''}`,
      profileData?.socialLinks?.find((l) => l?.platform === 'LinkedIn')?.url ? `URL:${profileData?.socialLinks?.find((l) => l?.platform === 'LinkedIn')?.url}` : '',
      'END:VCARD'
    ]
      .filter(Boolean)
      .join('\n');

    const blob = new Blob([vCard], { type: 'text/vcard' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${profileData?.fullName?.replace(/\s+/g, '_')}.vcf`;
    link.click();
    URL.revokeObjectURL(link.href);

    // Track analytics
    trackAnalytics('contact_download', {
      employeeId: profileData?.employeeId,
      format: 'vcard'
    });
  };

  const handleDownloadQRCode = useCallback(async () => {
    if (!qrCodeRef.current) return;

    try {
      // Get the SVG element from the QR code container
      const svgElement = qrCodeRef.current.querySelector('svg');
      if (!svgElement) return;

      // Create a canvas element
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // Set canvas dimensions (add padding for better appearance)
      const padding = 40;
      const qrSize = 200;
      canvas.width = qrSize + (padding * 2);
      canvas.height = qrSize + (padding * 2);

      // Fill white background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Convert SVG to data URL
      const svgData = new XMLSerializer().serializeToString(svgElement);
      const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
      const svgUrl = URL.createObjectURL(svgBlob);

      // Create image from SVG
      const img = new Image();
      img.onload = () => {
        // Draw QR code centered on canvas
        ctx.drawImage(img, padding, padding, qrSize, qrSize);
        URL.revokeObjectURL(svgUrl);

        // Download the image
        const pngUrl = canvas.toDataURL('image/png');
        const downloadLink = document.createElement('a');
        downloadLink.href = pngUrl;
        downloadLink.download = `${profileData?.fullName?.replace(/\s+/g, '_')}_QR_Code.png`;
        downloadLink.click();

        // Track analytics
        trackAnalytics('qr_download', { employeeId: profileData?.employeeId });
      };
      img.onerror = () => {
        URL.revokeObjectURL(svgUrl);
        console.error('Failed to load QR code image');
      };
      img.src = svgUrl;
    } catch (error) {
      console.error('Failed to download QR code:', error);
    }
  }, [profileData?.fullName, profileData?.employeeId]);

  const handleProfileSwitch = (index: number) => {
    setCurrentProfileIndex(index);
    trackAnalytics('profile_switch', {
      fromProfile: profiles?.[currentProfileIndex]?.profileId,
      toProfile: profiles?.[index]?.profileId
    });
  };

  // Language is locked to the profile's configured language (set by the owner
  // during persona configuration). Force i18n to it whenever it changes so the
  // entire public profile renders in that language regardless of the viewer's
  // own preference; viewers no longer have a toggle on this page.
  useEffect(() => {
    const locked = profileData?.language;
    if (locked && locked !== i18n.language) {
      i18n.changeLanguage(locked).catch((err) => console.error('Failed to lock language', err));
    }
  }, [profileData?.language, i18n]);

  // Get theme colors
  const getThemeColors = (): ThemeColors => {
    const scheme = profileData?.colorScheme || { primary: 'blue', secondary: 'indigo' };
    const colorMap: Record<string, ThemeColors> = {
      blue: { from: 'from-blue-600', to: 'to-indigo-600', bgLight: 'from-blue-50', bg: 'bg-blue-50', text: 'text-blue-700', badge: 'bg-blue-100' },
      purple: { from: 'from-purple-600', to: 'to-pink-600', bgLight: 'from-purple-50', bg: 'bg-purple-50', text: 'text-purple-700', badge: 'bg-purple-100' },
      green: { from: 'from-green-600', to: 'to-emerald-600', bgLight: 'from-green-50', bg: 'bg-green-50', text: 'text-green-700', badge: 'bg-green-100' },
      red: { from: 'from-red-600', to: 'to-orange-600', bgLight: 'from-red-50', bg: 'bg-red-50', text: 'text-red-700', badge: 'bg-red-100' },
      indigo: { from: 'from-indigo-600', to: 'to-blue-600', bgLight: 'from-indigo-50', bg: 'bg-indigo-50', text: 'text-indigo-700', badge: 'bg-indigo-100' }
    };
    return colorMap[scheme.primary] || colorMap.blue;
  };

  const themeColors = getThemeColors();

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-primary mx-auto mb-4 animate-spin" />
          <h2 className="text-xl font-semibold text-foreground mb-2">{t('Loading Profile...')}</h2>
          <p className="text-muted-foreground">{t('Please wait while we fetch the profile details.')}</p>
        </div>
      </div>
    );
  }

  // True not-found (404) — a nonexistent/non-public slug. Not-found copy with
  // NO retry CTA (retrying can't resolve a profile that doesn't exist).
  if (notFound) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-foreground mb-2">{t('Profile Not Found')}</h2>
          <p className="text-muted-foreground">{t('The requested profile could not be found.')}</p>
        </div>
      </div>
    );
  }

  // Error state — a transient failure the visitor can retry.
  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-destructive mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-foreground mb-2">{t('Something went wrong')}</h2>
          <p className="text-muted-foreground mb-4">{error}</p>
          <Button
            onClick={() => window.location.reload()}
          >
            {t('Try Again')}
          </Button>
        </div>
      </div>
    );
  }

  // Show "Configure Profile" CTA for authenticated users without a profile
  if (showNoProfileCTA) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <User className="w-10 h-10 text-primary" />
          </div>
          <h2 className="text-2xl font-bold text-foreground mb-3">{t('No Public Profile Configured')}</h2>
          <p className="text-muted-foreground mb-6">
            {t("You haven't set up your public profile yet. Create one to share your professional identity with others.")}
          </p>
          <Button
            onClick={() => navigate('/profile-configuration-center')}
            className="gap-2 shadow-lg shadow-primary/25"
          >
            <Settings className="w-5 h-5" />
            {t('Configure Profile')}
          </Button>
        </div>
      </div>
    );
  }

  // No profile data state
  if (!profileData) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-foreground mb-2">{t('Profile Not Found')}</h2>
          <p className="text-muted-foreground">{t('The requested profile could not be found.')}</p>
        </div>
      </div>
    );
  }

  // Profile not public state
  if (!profileData?.isPublic) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Lock className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-foreground mb-2">{t('Profile Not Public')}</h2>
          <p className="text-muted-foreground">{t('This employee profile is not available for public viewing.')}</p>
        </div>
      </div>
    );
  }

  // Show the persona dropdown only on the internal viewer (no slug) when the
  // owner has more than one persona to flip between.
  const showPersonaSwitcher = !slug && profiles?.length > 1;

  // Page background must derive from the SAME accent source as the hero banner
  // and skill chips: the HR brand tint when branding is customized, otherwise
  // the employee Color Scheme's Tailwind gradient. Keeps all three coordinated.
  const pageBg = getProfileBackgroundStyle(profileData?.branding);

  return (
    // The outer app shell uses `h-screen overflow-hidden`, so this route must
    // own its own scroll container — otherwise the sticky bar would freeze and
    // the rest of the content would clip. `h-screen overflow-y-auto` makes the
    // page scrollable and lets the bar's `sticky top-0` pin against it.
    <div
      className={`h-screen overflow-y-auto ${pageBg.customized ? 'bg-background' : `bg-gradient-to-br ${themeColors?.bgLight} via-background to-muted`}`}
      style={pageBg.style}
    >
        {/* Sticky Action Bar — emphasised, contrasting surface that pins to the
            top of the scroll container so the visitor's primary CTAs (Save to
            Contacts, Leave your contact, Share, Report) are always one tap
            away. */}
        <div
          data-testid="public-profile-sticky-actions"
          className="sticky top-0 z-40 bg-background/95 backdrop-blur-md border-b border-border shadow-sm"
        >
          <div className="max-w-5xl mx-auto px-3 sm:px-6 py-3">
            <div className="flex items-center justify-between gap-2 sm:gap-3 flex-wrap">
              {/* Persona switcher (internal view only) */}
              {showPersonaSwitcher ? (
                <div className="flex items-center gap-2 min-w-0">
                  <label htmlFor="persona-switcher" className="text-xs font-medium text-muted-foreground whitespace-nowrap hidden sm:inline">
                    {t('Persona')}
                  </label>
                  <select
                    id="persona-switcher"
                    data-testid="persona-switcher"
                    value={currentProfileIndex}
                    onChange={(e) => handleProfileSwitch(Number(e.target.value))}
                    className="ps-3 pe-8 py-2 bg-card border border-input rounded-lg text-sm font-medium text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary max-w-[60vw] sm:max-w-none"
                  >
                    {profiles?.map((profile, index) => (
                      <option key={profile?.profileId} value={index}>
                        {profile?.profileType} {t('Profile')}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="hidden sm:block" />
              )}

              {/* Primary CTAs — emphasised on the sticky strip. The two
                  outreach actions ("Leave your contact", "Save to Contacts")
                  always render with their labels, including on mobile. */}
              <div className="flex items-center gap-2 sm:gap-3 flex-wrap justify-end flex-1">
                <Button
                  data-testid="sticky-leave-contact"
                  variant="default"
                  onClick={() => setShowContactDrawer(true)}
                  className="gap-2 shadow-md shadow-primary/30"
                  title={t('Leave your contact')}
                >
                  <Send className="w-4 h-4" />
                  <span>{t('Leave your contact')}</span>
                </Button>
                <Button
                  data-testid="sticky-save-contact"
                  variant="outline"
                  onClick={generateVCard}
                  className="gap-2 font-semibold"
                  title={t('saveToContacts')}
                >
                  <Download className="w-4 h-4" />
                  <span>{t('saveToContacts')}</span>
                </Button>
                <Button
                  data-testid="sticky-share"
                  variant="outline"
                  size="icon"
                  onClick={() => setShowShareMenu(true)}
                  title={t('share')}
                  aria-label={t('share')}
                >
                  <Share2 className="w-4 h-4" />
                </Button>
                <Button
                  data-testid="sticky-report"
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowReportModal(true)}
                  className="text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                  title={t('report')}
                  aria-label={t('report')}
                >
                  <Flag className="w-[18px] h-[18px]" />
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-8 pt-4">
          {/* Main Profile Card — rendered through the shared ProfileCard
              component so the configuration center's Live Preview (which
              also uses ProfileCard, variant="preview") is a 1:1 visual
              replica of what visitors actually see here. Any future tweak
              to header, contact, skills, or social-links rendering only
              needs to be made in ProfileCard. */}
          <div ref={profileRef} className="mb-8">
            <ProfileCard
              data={{
                firstName: profileData.firstName,
                lastName: profileData.lastName,
                fullName: profileData.fullName,
                jobTitle: profileData.jobTitle,
                department: profileData.department,
                location: profileData.location,
                bio: profileData.bio,
                email: profileData.email,
                phone: profileData.phone,
                photo: profileData.photo,
                companyName: profileData.companyName,
                // Respect the persona/company `showCompanyLogo` flag — null
                // suppresses the logo entirely in the header.
                companyLogo: (profileData.branding?.showCompanyLogo === false)
                  ? null
                  : (profileData.branding?.companyLogoUrl ?? profileData.companyLogo ?? null),
                skills: profileData.skills,
                achievements: profileData.achievements.map((a) => ({
                  title: a.title,
                  date: a.date,
                  icon: a.icon,
                })),
                socialLinks: profileData.socialLinks.map((s) => ({
                  platform: s.platform,
                  url: s.url,
                  icon: s.icon,
                })),
                showEmail: profileData.showEmail,
                showPhone: profileData.showPhone,
                showSkills: profileData.showSkills,
                showAchievements: profileData.showAchievements,
                isVerified: profileData.isVerified,
                colorScheme: profileData.colorScheme.primary,
                profileUrl,
                slug: profileData.slug,
                branding: profileData.branding ? {
                  primaryColor: profileData.branding.primaryColor,
                  secondaryColor: profileData.branding.secondaryColor,
                  disclaimerText: profileData.branding.disclaimerText,
                  footerText: profileData.branding.footerText,
                  showDisclaimer: profileData.branding.showDisclaimer,
                } : undefined,
              } as ProfileDisplayData}
              variant="full"
              onLeaveContact={() => setShowContactDrawer(true)}
              t={t}
            />
          </div>

          {/* QR Code Section */}
          <Card className="rounded-2xl shadow-lg">
            <CardContent className="p-8">
            <div className="text-center">
              <h2 className="text-xl font-bold text-foreground mb-2">{t('scanToConnect')}</h2>
              <p className="text-muted-foreground mb-6">{t('shareQRCode')}</p>

              <div ref={qrCodeRef} className="inline-block p-6 bg-card rounded-xl border border-border shadow-sm">
                <QRCodeSVG
                  value={profileUrl}
                  size={200}
                  level="H"
                  includeMargin={false}
                  bgColor="#ffffff"
                  fgColor="#000000"
                  imageSettings={profileData?.companyLogo ? {
                    src: getImageUrl(profileData.companyLogo) || '',
                    x: undefined,
                    y: undefined,
                    height: 40,
                    width: 40,
                    excavate: true,
                  } : undefined}
                />
              </div>

              <div className="mt-6">
                <Button
                  variant="outline"
                  onClick={handleDownloadQRCode}
                  className="gap-2"
                >
                  <Download className="w-4 h-4" />
                  {t('downloadQR')}
                </Button>
              </div>
            </div>
            </CardContent>
          </Card>

          {/* Company Disclaimer (from HR Settings) */}
          {profileData?.branding?.showDisclaimer && profileData?.branding?.disclaimerText && (
            <div className="mt-8 p-4 bg-muted rounded-lg border border-border">
              <p className="text-sm text-muted-foreground text-center">
                {profileData.branding.disclaimerText}
              </p>
            </div>
          )}

          {/* Company Footer Text (from HR Settings) */}
          {profileData?.branding?.footerText && (
            <div className="text-center mt-6 text-sm text-muted-foreground">
              <p>{profileData.branding.footerText}</p>
            </div>
          )}

          {/* Footer */}
          <div className="text-center mt-8 text-sm text-muted-foreground">
            <p>
              {t('Powered by')}{' '}
              <a
                href="https://mowazaf.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:text-primary/80 hover:underline font-medium"
              >
                Mowazaf
              </a>
            </p>
          </div>
        </div>

      {/* Share Menu Modal */}
      {showShareMenu && (
        <ShareMenuModal
          profileData={profileData}
          profileUrl={profileUrl}
          onClose={() => setShowShareMenu(false)}
          onShare={(platform) => {
            trackAnalytics('profile_share', {
              platform,
              employeeId: profileData?.employeeId
            });
          }}
        />
      )}

      {/* Report Modal */}
      {showReportModal && (
        <ReportModal
          profileData={profileData}
          onClose={() => setShowReportModal(false)}
          onSubmit={(reason, details) => {
            trackAnalytics('profile_report', {
              employeeId: profileData?.employeeId,
              reason,
              details
            });
            setShowReportModal(false);
          }}
        />
      )}

      {/* Contact Drawer */}
      <ContactDrawer
        isOpen={showContactDrawer}
        onClose={() => setShowContactDrawer(false)}
        employeeName={profileData?.fullName}
        profileSlug={profileData?.slug || ''}
        source="direct"
      />

      {/* Copied Toast */}
      {copiedToast && (
        <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 bg-success text-success-foreground px-6 py-3 rounded-lg shadow-lg flex items-center gap-2 animate-fade-in">
          <Check className="w-5 h-5" />
          <span>{t('Link copied to clipboard!')}</span>
        </div>
      )}
    </div>
  );
};

export default PublicProfileDisplay;
